<template>
  <div class="user-level">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>会员等级管理</span>
          <div>
            <el-button type="success" @click="handleCheckUpgrade">等级重算</el-button>
            <el-button type="primary" @click="handleAdd">添加等级</el-button>
          </div>
        </div>
      </template>
      
      <el-table :data="levelList" style="width: 100%" row-key="id">
        <el-table-column prop="id" label="ID" width="60" />
        <el-table-column label="标识" width="100">
          <template #default="{ row }">
            <div style="display:flex;align-items:center;gap:6px;">
              <div :style="{width:'16px',height:'16px',borderRadius:'50%',background:row.color || '#999',display:'inline-block'}" />
              <span>{{ row.name }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="折扣" width="80">
          <template #default="{ row }">
            {{ (parseFloat(row.discount) * 10).toFixed(1) }}折
          </template>
        </el-table-column>
        <el-table-column prop="min_points" label="最低积分" width="90" />
        <el-table-column label="最低消费" width="90">
          <template #default="{ row }">
            {{ parseFloat(row.min_spending || 0).toFixed(0) }}元
          </template>
        </el-table-column>
        <el-table-column prop="sort_order" label="排序" width="60" />
        <el-table-column label="自动升级" width="80">
          <template #default="{ row }">
            <el-tag :type="row.auto_upgrade ? 'success' : 'info'" size="small">{{ row.auto_upgrade ? '是' : '否' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="自动降级" width="80">
          <template #default="{ row }">
            <el-tag :type="row.auto_downgrade ? 'warning' : 'info'" size="small">{{ row.auto_downgrade ? row.downgrade_cycle : '否' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="description" label="描述" />
        <el-table-column label="操作" width="150" fixed="right">
          <template #default="{ row }">
            <el-button type="primary" size="small" @click="handleEdit(row)">编辑</el-button>
            <el-button type="danger" size="small" @click="handleDelete(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>
    
    <!-- 编辑/添加对话框 -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="600px">
      <el-form :model="form" label-width="120px">
        <el-form-item label="等级名称">
          <el-input v-model="form.name" placeholder="如：银卡会员" />
        </el-form-item>
        <el-form-item label="颜色标识">
          <el-color-picker v-model="form.color" />
          <span style="margin-left:8px;">{{ form.color || '未设置' }}</span>
        </el-form-item>
        <el-form-item label="折扣率">
          <el-input-number v-model="form.discount" :min="0.01" :max="1.00" :step="0.01" :precision="2" />
          <span style="margin-left:8px;">{{ (form.discount * 10).toFixed(1) }}折</span>
        </el-form-item>
        <el-form-item label="最低积分">
          <el-input-number v-model="form.min_points" :min="0" />
        </el-form-item>
        <el-form-item label="最低消费(元)">
          <el-input-number v-model="form.min_spending" :min="0" :precision="2" />
        </el-form-item>
        <el-form-item label="排序">
          <el-input-number v-model="form.sort_order" :min="0" />
        </el-form-item>
        <el-form-item label="自动升级">
          <el-switch v-model="form.auto_upgrade" />
        </el-form-item>
        <el-form-item label="自动降级">
          <el-switch v-model="form.auto_downgrade" />
        </el-form-item>
        <el-form-item label="降级周期" v-if="form.auto_downgrade">
          <el-select v-model="form.downgrade_cycle" style="width:150px;">
            <el-option label="每月" value="month" />
            <el-option label="每季度" value="quarter" />
            <el-option label="每年" value="year" />
          </el-select>
        </el-form-item>
        <el-form-item label="描述">
          <el-input v-model="form.description" type="textarea" :rows="2" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSave">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import request from '../../utils/request'

export default {
  name: 'UserLevel',
  setup() {
    const levelList = ref([])
    const dialogVisible = ref(false)
    const dialogTitle = ref('')
    const form = ref({})
    
    const fetchLevels = async () => {
      try {
        const res = await request.get('/api/admin/member-levels')
        levelList.value = res.data || []
      } catch (error) {
        ElMessage.error('获取会员等级失败')
      }
    }
    
    const handleAdd = () => {
      form.value = { discount: 1.00, min_points: 0, min_spending: 0, sort_order: 0, auto_upgrade: true, auto_downgrade: false, downgrade_cycle: '', color: '' }
      dialogTitle.value = '添加等级'
      dialogVisible.value = true
    }
    
    const handleEdit = (row) => {
      form.value = { ...row, downgrade_cycle: row.downgrade_cycle || '', auto_upgrade: !!row.auto_upgrade, auto_downgrade: !!row.auto_downgrade }
      dialogTitle.value = '编辑等级'
      dialogVisible.value = true
    }
    
    const handleSave = async () => {
      try {
        await request.post('/api/admin/member-levels', form.value)
        ElMessage.success('保存成功')
        dialogVisible.value = false
        fetchLevels()
      } catch (error) {
        ElMessage.error('保存失败')
      }
    }
    
    const handleDelete = async (row) => {
      try {
        await ElMessageBox.confirm('确认删除该等级？', '提示', { type: 'warning' })
        await request.delete('/api/admin/member-levels/' + row.id)
        ElMessage.success('删除成功')
        fetchLevels()
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('删除失败')
        }
      }
    }
    
    const handleCheckUpgrade = async () => {
      try {
        await ElMessageBox.confirm('将根据积分和消费额重新计算所有用户的等级，确认执行？', '等级重算', { type: 'warning' })
        const res = await request.post('/api/admin/member-levels/check-upgrade')
        ElMessage.success(res.message || '重算完成')
        fetchLevels()
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('重算失败')
        }
      }
    }
    
    onMounted(() => {
      fetchLevels()
    })
    
    return {
      levelList,
      dialogVisible,
      dialogTitle,
      form,
      handleAdd,
      handleEdit,
      handleSave,
      handleDelete,
      handleCheckUpgrade
    }
  }
}
</script>

<style scoped>
.user-level {
  padding: 8px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
}

.card-header span {
  font-weight: 600;
  font-size: 16px;
  color: #303133;
}
</style>
